<h2 mat-dialog-title>Kalender-indstillinger</h2>

<div class="settings-impact-banner" role="note">
  <mat-icon class="banner-icon">info</mat-icon>
  <div>
    <strong>Vigtigt:</strong> Når du bruger «Anvend for hele året» under gruppe-rotation
    eller uge-rotation, overskrives gruppevalg for hele det valgte år og tilhørende
    events opdateres. Det kan <strong>ændre brugernes tildeling til øvelser</strong>
    på berørte dage. Gem indstillinger bagefter for at bevare øvrige valg.
  </div>
</div>

<mat-dialog-content class="settings-content">
  <mat-tab-group class="settings-tabs" animationDuration="0ms">
    <mat-tab label="Visning">
      <section class="settings-section">
        <div class="section-head">
          <h3>Kolonner — lokationer</h3>
          <span class="section-hint"
            >Vælg hvilke lokationer der vises før «Andet»-kolonnen.</span
          >
        </div>

        <div class="location-toolbar">
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Søg lokation</mat-label>
            <input
              matInput
              [(ngModel)]="locationSearch"
              (ngModelChange)="applyLocationFilter()"
            />
          </mat-form-field>
          <span class="selected-count">{{ selectedLocationCount }} valgt</span>
          <button
            mat-stroked-button
            type="button"
            (click)="selectAllVisibleLocations()"
            [disabled]="loadingLocations || filteredLocations.length === 0"
          >
            Vælg synlige
          </button>
          <button
            mat-stroked-button
            type="button"
            (click)="clearAllLocations()"
            [disabled]="selectedLocationCount === 0"
          >
            Ryd
          </button>
        </div>

        @if (loadingLocations) {
        <div class="loading-row">
          <mat-spinner diameter="28"></mat-spinner>
          <span>Henter lokationer…</span>
        </div>
        } @if (!loadingLocations && loadError) {
        <p class="error-text">{{ loadError }}</p>
        } @if (!loadingLocations && !loadError) {
        <div class="location-list">
          @for (loc of filteredLocations; track loc) {
          <label class="location-row">
            <mat-checkbox
              [checked]="isLocationSelected(loc.guid)"
              (change)="toggleLocation(loc.guid, $event.checked)"
            >
              {{ loc.name }}
            </mat-checkbox>
          </label>
          } @if (filteredLocations.length === 0) {
          <p class="empty-hint">Ingen lokationer matcher søgningen.</p>
          }
        </div>
        }
      </section>

      @if (groupsEnabled) {
      <mat-divider></mat-divider>
      <section class="settings-section">
        <mat-checkbox [(ngModel)]="localSettings.showGroupColumn"
          >Vis gruppe-kolonne</mat-checkbox
        >
      </section>
      }

      <mat-divider></mat-divider>

      <section class="settings-section">
        <div class="section-head">
          <h3>Skabelon-farver</h3>
          <span class="section-hint"
            >Farver bruges kun i denne kalender som baggrund fra ugeplanen.</span
          >
        </div>

        @if (loadingTemplates) {
        <div class="loading-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Henter skabeloner…</span>
        </div>
        } @if (!loadingTemplates) {
        <div class="color-list">
          @for (tpl of templates; track tpl) {
          <div class="color-row">
            <span
              class="color-swatch"
              [style.background-color]="getTemplateColor(tpl.template_id)"
            ></span>
            <span class="tpl-name">{{ tpl.name }}</span>
            <input
              type="color"
              class="color-input"
              [value]="getTemplateColor(tpl.template_id)"
              (input)="setTemplateColor(tpl.template_id, $any($event.target).value)"
            />
          </div>
          } @if (templates.length === 0) {
          <p class="empty-hint">Ingen skabeloner oprettet endnu.</p>
          }
        </div>
        }
      </section>
    </mat-tab>

    @if (groupsEnabled) {
    <mat-tab label="Gruppefordeling">
      <section class="settings-section rotation-mode-section">
        <mat-button-toggle-group
          [(ngModel)]="rotationEditTab"
          (ngModelChange)="onRotationEditTabChange($event)"
          aria-label="Rotationstype"
        >
          <mat-button-toggle value="group">Gruppe-rotation</mat-button-toggle>
          <mat-button-toggle value="week">Uge-rotation</mat-button-toggle>
        </mat-button-toggle-group>
      </section>

      @if (rotationEditTab === 'group') {
      <section class="settings-section">
        <div class="section-head">
          <h3>Gruppe-rotation</h3>
          <span class="section-hint">
            Byg en gentagen rækkefølge af grupper for et helt år. Eksempel: Gruppe 1
            → Gruppe 3 → Gruppe 2 giver 1, 3, 2, 1, 3, 2 … på alle årets dage.
          </span>
        </div>
        <div class="rotation-toolbar">
          <mat-form-field appearance="outline" class="year-field">
            <mat-label>År</mat-label>
            <mat-select
              [(ngModel)]="localSettings.groupRotationYear"
              (selectionChange)="onGroupRotationYearChange()"
            >
              @for (y of yearOptions; track y) {
              <mat-option [value]="y">{{ y }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="rotation-add-field">
            <mat-label>Tilføj gruppe</mat-label>
            <mat-select [(ngModel)]="groupRotationAddId">
              <mat-option [value]="null">Vælg gruppe</mat-option>
              @for (g of groups; track g) {
              <mat-option [value]="g.group_id">{{ g.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            mat-stroked-button
            type="button"
            (click)="addGroupToRotation()"
            [disabled]="groupRotationAddId == null || groups.length === 0"
          >
            Tilføj til rækkefølge
          </button>
        </div>
        @if (groups.length === 0) {
        <p class="empty-hint">Ingen grupper fundet i organisationen.</p>
        } @if (groupRotationSequence.length > 0) {
        <ol class="rotation-sequence">
          @for (groupId of groupRotationSequence; track groupId; let i = $index) {
          <li class="rotation-item">
            <span class="rotation-index">{{ i + 1 }}.</span>
            <span class="rotation-name">{{ getGroupName(groupId) }}</span>
            <span class="rotation-actions">
              <button
                mat-icon-button
                type="button"
                (click)="moveGroupInRotation(i, -1)"
                [disabled]="i === 0"
                aria-label="Flyt op"
              >
                <mat-icon>arrow_upward</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                (click)="moveGroupInRotation(i, 1)"
                [disabled]="i === groupRotationSequence.length - 1"
                aria-label="Flyt ned"
              >
                <mat-icon>arrow_downward</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                (click)="removeGroupFromRotation(i)"
                aria-label="Fjern"
              >
                <mat-icon>close</mat-icon>
              </button>
            </span>
          </li>
          }
        </ol>
        } @if (rotationPreview) {
        <p class="rotation-preview">
          Rotation: <strong>{{ rotationPreview }}</strong> → gentages …
        </p>
        }
        <div class="rotation-actions-bar">
          <button
            mat-flat-button
            color="warn"
            type="button"
            (click)="applyGroupRotation()"
            [disabled]="groupRotationSequence.length === 0 || applyingRotation"
          >
            {{
              applyingRotation
                ? "Anvender…"
                : "Anvend for hele " + localSettings.groupRotationYear
            }}
          </button>
          <button
            mat-button
            type="button"
            (click)="clearGroupRotationSequence()"
            [disabled]="groupRotationSequence.length === 0"
          >
            Ryd rækkefølge
          </button>
        </div>
        @if (rotationFeedback) {
        <p class="rotation-feedback">{{ rotationFeedback }}</p>
        }
      </section>
      }

      @if (rotationEditTab === 'week') {
      <section class="settings-section">
        <div class="section-head">
          <h3>Uge-rotation</h3>
          <span class="section-hint">
            Vælg antal uger og hvilke grupper der gælder på hver ugedag. Dage uden
            valgt gruppe er tomme, indtil du tildeler en. Cyklussen gentages for hele
            året fra startdatoen for uge 1.
          </span>
        </div>
        <div class="rotation-toolbar">
          <mat-form-field appearance="outline" class="year-field">
            <mat-label>År</mat-label>
            <mat-select
              [(ngModel)]="localSettings.weekRotationYear"
              (selectionChange)="onWeekRotationYearChange()"
            >
              @for (y of yearOptions; track y) {
              <mat-option [value]="y">{{ y }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="week-count-field">
            <mat-label>Antal uger</mat-label>
            <input
              matInput
              type="number"
              min="1"
              [max]="maxWeekRotationWeeks"
              [ngModel]="weekRotationConfig?.weekCount"
              (ngModelChange)="onWeekCountChange($event)"
            />
          </mat-form-field>
          <mat-form-field appearance="outline" class="anchor-date-field">
            <mat-label>Startdato for uge 1</mat-label>
            <input
              matInput
              type="date"
              [ngModel]="weekRotationConfig?.anchorDate"
              (ngModelChange)="onWeekRotationAnchorChange($event)"
            />
          </mat-form-field>
        </div>

        @if (weekRotationConfig; as cfg) {
        <div class="week-rotation-grid-scroll">
          <table class="week-rotation-table">
            <thead>
              <tr>
                <th class="week-col">Uge</th>
                @for (wd of weekdayLabels; track wd.key) {
                <th>{{ wd.label }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (wi of weekRotationWeekIndices; track wi) {
              <tr>
                <td class="week-col">Uge {{ wi + 1 }}</td>
                @for (wd of weekdayLabels; track wd.key) {
                <td>
                  <mat-form-field appearance="outline" class="cell-select" subscriptSizing="dynamic">
                    <mat-select
                      multiple
                      [ngModel]="getWeekRotationGroup(wi, wd.key)"
                      (ngModelChange)="setWeekRotationGroup(wi, wd.key, $event)"
                    >
                      @for (g of groups; track g.group_id) {
                      <mat-option [value]="g.group_id">{{ g.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </td>
                }
              </tr>
              }
            </tbody>
          </table>
        </div>
        }

        <div class="rotation-actions-bar">
          <button
            mat-flat-button
            color="warn"
            type="button"
            (click)="applyWeekRotation()"
            [disabled]="!weekRotationConfig || applyingWeekRotation"
          >
            {{
              applyingWeekRotation
                ? "Anvender…"
                : "Anvend for hele " + localSettings.weekRotationYear
            }}
          </button>
        </div>
        @if (weekRotationFeedback) {
        <p class="rotation-feedback">{{ weekRotationFeedback }}</p>
        }
      </section>
      }
    </mat-tab>
    }

    <mat-tab label="Standard ugeplan">
      <section class="settings-section">
        @if (rotationEditTab === 'group') {
        <div class="section-head">
          <h3>Standard ugeplan (gruppe-rotation)</h3>
          <span class="section-hint">
            Én uge gentages hele året. Overblik for alle valgte lokationer og «Andet».
            Farver og standard-skabelon ved dag-klik følger den pågældende kolonne.
          </span>
        </div>
        } @else {
        <div class="section-head">
          <h3>Standard ugeplan (uge-rotation)</h3>
          <span class="section-hint">
            Plan for hver uge i cyklussen ({{ weekRotationConfig?.weekCount ?? "—" }}
            uger). Bruges når uge-rotation er aktiv for året.
          </span>
        </div>
        }

        @if (planLocationOptions.length === 0) {
        <p class="empty-hint">
          Vælg mindst én lokation under «Visning» for at konfigurere ugeplan.
        </p>
        }

        @if (planLocationOptions.length > 0 && rotationEditTab === 'group') {
        <div class="plan-toolbar">
          <mat-form-field appearance="outline" class="year-field">
            <mat-label>År</mat-label>
            <mat-select
              [(ngModel)]="localSettings.weeklyPlanYear"
              (selectionChange)="onPlanYearChange()"
            >
              @for (y of yearOptions; track y) {
              <mat-option [value]="y">{{ y }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
        <div class="plan-overview-scroll">
          <table class="plan-overview-table">
            <thead>
              <tr>
                <th class="day-col">Dag</th>
                @for (loc of planLocationOptions; track loc.key) {
                <th class="loc-col" [matTooltip]="loc.name">{{ loc.name }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (wd of weekdayLabels; track wd.key) {
              <tr>
                <td class="day-col">{{ wd.label }}</td>
                @for (loc of planLocationOptions; track loc.key) {
                <td class="loc-col">
                  <mat-form-field appearance="outline" class="cell-select" subscriptSizing="dynamic">
                    <mat-select
                      [value]="getWeeklyPlanDayForLocation(loc.key, wd.key)"
                      (selectionChange)="setWeeklyPlanDayForLocation(loc.key, wd.key, $event.value)"
                      [disabled]="loadingTemplates"
                    >
                      <mat-select-trigger>
                        @if (getWeeklyPlanDayForLocation(loc.key, wd.key); as tplId) {
                        <span class="cell-trigger">
                          <span class="option-swatch" [style.background-color]="getTemplateColor(tplId)"></span>
                          <span class="cell-tpl-name">{{ getTemplateName(tplId) }}</span>
                        </span>
                        } @else { — }
                      </mat-select-trigger>
                      <mat-option [value]="null">Ingen</mat-option>
                      @for (tpl of templates; track tpl.template_id) {
                      <mat-option [value]="tpl.template_id">
                        <span class="option-with-color">
                          <span class="option-swatch" [style.background-color]="getTemplateColor(tpl.template_id)"></span>
                          {{ tpl.name }}
                        </span>
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </td>
                }
              </tr>
              }
            </tbody>
          </table>
        </div>
        }

        @if (planLocationOptions.length > 0 && rotationEditTab === 'week') {
        <div class="plan-toolbar">
          <mat-form-field appearance="outline" class="year-field">
            <mat-label>År</mat-label>
            <mat-select
              [(ngModel)]="localSettings.weekRotationYear"
              (selectionChange)="onWeekPlanYearChange()"
            >
              @for (y of yearOptions; track y) {
              <mat-option [value]="y">{{ y }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
        <div class="plan-overview-scroll plan-overview-scroll--tall">
          <table class="plan-overview-table">
            <thead>
              <tr>
                <th class="day-col">Periode</th>
                @for (loc of planLocationOptions; track loc.key) {
                <th class="loc-col" [matTooltip]="loc.name">{{ loc.name }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of weekRotationPlanRowLabels; track row.label) {
              <tr>
                <td class="day-col">{{ row.label }}</td>
                @for (loc of planLocationOptions; track loc.key) {
                <td class="loc-col">
                  <mat-form-field appearance="outline" class="cell-select" subscriptSizing="dynamic">
                    <mat-select
                      [value]="getWeekRotationWeeklyPlanTemplate(loc.key, row.weekIndex, row.dayKey)"
                      (selectionChange)="setWeekRotationWeeklyPlanTemplate(loc.key, row.weekIndex, row.dayKey, $event.value)"
                      [disabled]="loadingTemplates"
                    >
                      <mat-select-trigger>
                        @if (getWeekRotationWeeklyPlanTemplate(loc.key, row.weekIndex, row.dayKey); as tplId) {
                        <span class="cell-trigger">
                          <span class="option-swatch" [style.background-color]="getTemplateColor(tplId)"></span>
                          <span class="cell-tpl-name">{{ getTemplateName(tplId) }}</span>
                        </span>
                        } @else { — }
                      </mat-select-trigger>
                      <mat-option [value]="null">Ingen</mat-option>
                      @for (tpl of templates; track tpl.template_id) {
                      <mat-option [value]="tpl.template_id">
                        <span class="option-with-color">
                          <span class="option-swatch" [style.background-color]="getTemplateColor(tpl.template_id)"></span>
                          {{ tpl.name }}
                        </span>
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </td>
                }
              </tr>
              }
            </tbody>
          </table>
        </div>
        }

        @if (planLocationOptions.length > 0) {
        <p class="plan-mode-hint">
          Viser ugeplan for
          <strong>{{
            rotationEditTab === 'week' ? 'uge-rotation' : 'gruppe-rotation'
          }}</strong
          >. Skift type under fanen «Gruppefordeling».
        </p>
        }
      </section>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="cancel()">Annuller</button>
  <button mat-flat-button color="primary" type="button" (click)="save()">
    Gem indstillinger
  </button>
</mat-dialog-actions>
